import {
  CurrencySummary,
  DEFAULT_CURRENCY_CODE,
  ExchangeConvertRequest,
  ForeignCurrencyEntity,
  ForeignCurrencyPayload,
} from '../../models/currency.model';
import { CreateSoldItemV2Dto } from '../../models/invoice-v2.model';

export function isForeignCurrency(currency: string | null | undefined): boolean {
  const code = (currency ?? DEFAULT_CURRENCY_CODE).trim().toUpperCase();
  return code !== '' && code !== DEFAULT_CURRENCY_CODE;
}

export function roundMoney(value: number, decimals = 2): number {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

export function convertForeignToIls(amount: number, rate: number): number {
  if (!Number.isFinite(amount) || !Number.isFinite(rate) || rate <= 0) {
    return 0;
  }
  return roundMoney(amount * rate);
}

export function convertIlsToForeign(amount: number, rate: number): number {
  if (!Number.isFinite(amount) || !Number.isFinite(rate) || rate <= 0) {
    return 0;
  }
  return roundMoney(amount / rate);
}

export function getCurrencyDisplayName(
  currency: { name_en?: string; name_ar?: string; name_he?: string },
  lang: string,
): string {
  if (lang === 'ar') {
    return currency.name_ar || currency.name_en || currency.name_he || '';
  }
  if (lang === 'he') {
    return currency.name_he || currency.name_en || currency.name_ar || '';
  }
  return currency.name_en || currency.name_he || currency.name_ar || '';
}

export interface InvoiceCurrencyFormSnapshot {
  currency: string;
  exchange_rate: number;
  exchange_rate_date: string;
  exchange_rate_source: string;
  exchange_rate_manually_changed?: boolean;
}

export interface InvoiceForeignTotals {
  subtotal_original: number;
  discount_original: number;
  vat_original: number;
  total_original: number;
}

export function buildForeignCurrencyPayload(
  form: InvoiceCurrencyFormSnapshot,
  totals: InvoiceForeignTotals,
): ForeignCurrencyPayload | undefined {
  if (!isForeignCurrency(form.currency)) {
    return undefined;
  }
  return {
    currency_code: form.currency,
    exchange_rate: roundMoney(form.exchange_rate, 4),
    exchange_rate_date: form.exchange_rate_date,
    exchange_rate_source: form.exchange_rate_source || 'BOI',
    ...(form.exchange_rate_manually_changed ? { exchange_rate_manually_changed: true } : {}),
    subtotal_original: roundMoney(totals.subtotal_original),
    discount_original: roundMoney(totals.discount_original),
    vat_original: roundMoney(totals.vat_original),
    total_original: roundMoney(totals.total_original),
  };
}

export function mapItemForSubmitWithCurrency(
  item: CreateSoldItemV2Dto & Record<string, unknown>,
  currency: string,
): CreateSoldItemV2Dto {
  const base: CreateSoldItemV2Dto = {
    catalog_id: item.catalog_id,
    category: item.category,
    measurement_unit_id: item.measurement_unit_id,
    description: item.description,
    item_name: item.item_name,
    measure_unit_description: item.measure_unit_description,
    quantity: item.quantity,
    price_per_unit: item.price_per_unit,
    sub_total: item.sub_total,
    discount: item.discount,
    total_amount: item.total_amount,
    vat_rate: item.vat_rate,
    vat_amount: item.vat_amount,
    product_id: item.product_id,
    isPresenting: item.isPresenting,
    isVatIncluded: item.isVatIncluded,
  };

  if (!isForeignCurrency(currency)) {
    return base;
  }

  return {
    ...base,
    price_per_unit_foreign: roundMoney(Number(item.price_per_unit_foreign ?? 0)),
    discount_foreign: roundMoney(Number(item.discount_foreign ?? 0)),
    sub_total_foreign: roundMoney(Number(item.sub_total_foreign ?? 0)),
    vat_amount_foreign: roundMoney(Number(item.vat_amount_foreign ?? 0)),
    total_amount_foreign: roundMoney(Number(item.total_amount_foreign ?? 0)),
  };
}

/** Read sold-item foreign amounts from API (`*_foreign`, with legacy `*_original` fallback). */
export function readSoldItemForeignFromApi(raw: Record<string, unknown>): {
  price_per_unit_foreign?: number;
  discount_foreign?: number;
  sub_total_foreign?: number;
  vat_amount_foreign?: number;
  total_amount_foreign?: number;
} {
  const num = (v: unknown): number | undefined =>
    v != null && v !== '' && Number.isFinite(Number(v)) ? Number(v) : undefined;

  return {
    price_per_unit_foreign:
      num(raw['price_per_unit_foreign']) ?? num(raw['price_per_unit_original']),
    discount_foreign: num(raw['discount_foreign']) ?? num(raw['discount_original']),
    sub_total_foreign: num(raw['sub_total_foreign']) ?? num(raw['sub_total_original']),
    vat_amount_foreign: num(raw['vat_amount_foreign']) ?? num(raw['vat_amount_original']),
    total_amount_foreign: num(raw['total_amount_foreign']) ?? num(raw['total_amount_original']),
  };
}

export function hasSoldItemForeignAmounts(raw: Record<string, unknown>): boolean {
  const f = readSoldItemForeignFromApi(raw);
  return (
    f.price_per_unit_foreign != null ||
    f.sub_total_foreign != null ||
    f.total_amount_foreign != null
  );
}

export interface ForeignLineSnapshot {
  index: number;
  price_per_unit_foreign: number;
  discount_foreign: number;
  sub_total_foreign: number;
  vat_amount_foreign: number;
  total_amount_foreign: number;
}

export function buildForeignToIlsConvertRequest(
  currency: string,
  date: string,
  exchangeRate: number | undefined,
  lines: ForeignLineSnapshot[],
  amounts: InvoiceForeignTotals,
): ExchangeConvertRequest {
  return {
    currency_code: currency,
    direction: 'FOREIGN_TO_ILS',
    date,
    ...(exchangeRate != null && exchangeRate > 0 ? { exchange_rate: exchangeRate } : {}),
    amounts: {
      subtotal: amounts.subtotal_original,
      discount: amounts.discount_original,
      vat: amounts.vat_original,
      total: amounts.total_original,
    },
    lines: lines.map((line) => ({
      index: line.index,
      price_per_unit: line.price_per_unit_foreign,
      discount: line.discount_foreign,
      sub_total: line.sub_total_foreign,
      vat_amount: line.vat_amount_foreign,
      total_amount: line.total_amount_foreign,
    })),
  };
}

export function resolveForeignCurrencyFromInvoice(invoice: Record<string, unknown>): ForeignCurrencyEntity | null {
  const raw = invoice['foreignCurrency'] ?? invoice['foreign_currency'];
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  return raw as ForeignCurrencyEntity;
}

export function resolveCurrencySummary(invoice: Record<string, unknown>): CurrencySummary | null {
  const raw = invoice['currency_summary'] ?? invoice['currencySummary'];
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  return raw as CurrencySummary;
}

/**
 * Document/display currency for UI (NOT `invoices.currency`, which is always ILS).
 * Priority: document_currency → currency_summary → foreignCurrency → ILS.
 */
export function resolveDocumentCurrency(invoice: Record<string, unknown>): string {
  const docCurrency = invoice['document_currency'] ?? invoice['documentCurrency'];
  if (typeof docCurrency === 'string' && docCurrency.trim()) {
    return docCurrency.trim().toUpperCase();
  }

  const summary = resolveCurrencySummary(invoice);
  if (summary?.currency_code?.trim()) {
    return summary.currency_code.trim().toUpperCase();
  }

  const foreign = resolveForeignCurrencyFromInvoice(invoice);
  if (foreign?.currency_code?.trim()) {
    return foreign.currency_code.trim().toUpperCase();
  }

  return DEFAULT_CURRENCY_CODE;
}

/** @deprecated Use resolveDocumentCurrency — invoice.currency is always ILS on the backend. */
export function resolveInvoiceCurrency(invoice: Record<string, unknown>): string {
  return resolveDocumentCurrency(invoice);
}

export function isForeignDocument(invoice: Record<string, unknown>): boolean {
  const summary = resolveCurrencySummary(invoice);
  if (summary?.is_foreign != null) {
    return !!summary.is_foreign;
  }
  return isForeignCurrency(resolveDocumentCurrency(invoice));
}

export function getDocumentCurrencySymbol(invoice: Record<string, unknown>): string {
  const summary = resolveCurrencySummary(invoice);
  if (summary?.currency_symbol?.trim()) {
    return summary.currency_symbol.trim();
  }
  const foreign = resolveForeignCurrencyFromInvoice(invoice);
  if (foreign?.currency_code && (foreign as ForeignCurrencyEntity & { currency_symbol?: string }).currency_symbol) {
    return String((foreign as ForeignCurrencyEntity & { currency_symbol?: string }).currency_symbol);
  }
  const code = resolveDocumentCurrency(invoice);
  return isForeignCurrency(code) ? code : '₪';
}
